import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCompetition } from '../../contexts/CompetitionContext';
import { LogOut, MoreHorizontal, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/admin', restrictedStages: [] },
  { id: 'competition', label: 'Competition', path: '/admin/competition', restrictedStages: [] },
  { id: 'participants', label: 'Participants', path: '/admin/participants', restrictedStages: [] },
  { id: 'questions', label: 'Questions', path: '/admin/questions', restrictedStages: [] },
  { id: 'results', label: 'Results', path: '/admin/results', restrictedStages: ['setup'] },
  { id: 'archive', label: 'Archive', path: '/admin/archive', restrictedStages: [] },
  { id: 'settings', label: 'Settings', path: '/admin/settings', restrictedStages: [] },
];

const mobileMainItems = ['dashboard', 'competition', 'participants', 'results'];
const mobileMoreItems = ['questions', 'archive', 'settings'];

const AdminLayout = () => {
  const { logout } = useAuth();
  const { stage } = useCompetition();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (e) {
      navigate('/admin/login');
    }
  };

  const isRestricted = (item) => {
    return item.restrictedStages.includes(stage);
  };

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-56 min-h-screen border-r-2 border-[#e2e8f0] bg-white fixed left-0 top-0 z-30">
        <div className="px-6 py-6 border-b-2 border-[#e2e8f0] flex items-center gap-3">
          <img src="/logo.png" alt="Brain Gym Jr." className="w-10 h-10 object-contain" />
          <div>
            <h1 className="text-lg font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Brain Gym Jr.
            </h1>
            <p className="text-xs text-[#64748b] mt-0.5">Admin Panel</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const restricted = isRestricted(item);
            return (
              <NavLink
                key={item.id}
                to={item.path}
                end={item.id === 'dashboard'}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                    restricted
                      ? 'text-[#cbd5e1] cursor-not-allowed pointer-events-none'
                      : isActive
                        ? 'bg-[#2565eb] text-white shadow-sm'
                        : 'text-[#475569] hover:bg-[#f1f5f9] hover:text-[#0f172a]'
                  }`
                }
                data-testid={`nav-${item.id}`}
                onClick={(e) => restricted && e.preventDefault()}
              >
                {item.label}
                {restricted && <span className="ml-2 text-xs opacity-60">(locked)</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t-2 border-[#e2e8f0]">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-[#ef4444] hover:bg-[#fef2f2] transition-all"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-56 pb-20 md:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#e2e8f0] z-40 flex items-center justify-around px-2 py-2">
        {mobileMainItems.map((id) => {
          const item = navItems.find(n => n.id === id);
          const restricted = isRestricted(item);
          return (
            <NavLink
              key={id}
              to={item.path}
              end={id === 'dashboard'}
              className={({ isActive }) =>
                `flex-1 text-center py-2 text-xs font-bold rounded-xl transition-all ${
                  restricted
                    ? 'text-[#cbd5e1] pointer-events-none'
                    : isActive
                      ? 'text-[#2565eb] bg-[#eff6ff]'
                      : 'text-[#64748b]'
                }`
              }
              data-testid={`mobile-nav-${id}`}
            >
              {item.label}
            </NavLink>
          );
        })}
        <button
          onClick={() => setMoreOpen(true)}
          className="flex-1 text-center py-2 text-xs font-bold text-[#64748b] rounded-xl"
          data-testid="mobile-nav-more"
        >
          <MoreHorizontal className="w-5 h-5 mx-auto mb-0.5" />
          More
        </button>
      </nav>

      {/* Mobile More Menu */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setMoreOpen(false)}>
          <div className="w-full bg-white rounded-t-3xl p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>More</h3>
              <button onClick={() => setMoreOpen(false)} className="p-2 rounded-xl hover:bg-[#f1f5f9]">
                <X className="w-5 h-5 text-[#64748b]" />
              </button>
            </div>
            <div className="space-y-1">
              {mobileMoreItems.map((id) => {
                const item = navItems.find(n => n.id === id);
                const restricted = isRestricted(item);
                return (
                  <NavLink
                    key={id}
                    to={item.path}
                    onClick={() => !restricted && setMoreOpen(false)}
                    className={`block px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                      restricted
                        ? 'text-[#cbd5e1] pointer-events-none'
                        : 'text-[#475569] hover:bg-[#f1f5f9]'
                    }`}
                    data-testid={`mobile-more-${id}`}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 rounded-xl text-sm font-semibold text-[#ef4444] hover:bg-[#fef2f2] transition-all"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
